import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStorageService } from '@/lib/service-context';
import { Button, Input, cn } from '@erp/ui';
import { ImageIcon, X, Loader2, Plus, Star } from '@/components/icons';

interface ImageGalleryProps {
  value: string[];
  onChange: (urls: string[]) => void;
  bucket: string;
  pathPrefix: string;
  maxImages?: number;
  className?: string;
  disabled?: boolean;
  /** 'grid' (default) = uniform grid; 'hero' = first image large, others as thumbnails below */
  variant?: 'grid' | 'hero';
}

export function ImageGallery({ value, onChange, bucket, pathPrefix, maxImages = 10, className, disabled, variant = 'grid' }: ImageGalleryProps) {
  const { t } = useTranslation();
  const storage = useStorageService();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList) => {
    const validFiles = Array.from(files).filter(
      (f) => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024,
    );
    if (validFiles.length === 0) return;

    const remaining = maxImages - value.length;
    const toUpload = validFiles.slice(0, remaining);
    if (toUpload.length === 0) return;

    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const file of toUpload) {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const path = `${pathPrefix}${crypto.randomUUID()}.${ext}`;
        const url = await storage.upload(bucket, path, file);
        urls.push(url);
      }
      onChange([...value, ...urls]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Image upload failed:', msg);
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (index: number) => {
    const url = value[index]!;
    try {
      const parsed = new URL(url);
      const pathParts = parsed.pathname.split(`/storage/v1/object/public/${bucket}/`);
      if (pathParts[1]) await storage.delete(bucket, pathParts[1]);
    } catch {
      // cleanup failed
    }
    onChange(value.filter((_, i) => i !== index));
  };

  const makePrimary = (index: number) => {
    if (index === 0) return;
    const next = [...value];
    const [moved] = next.splice(index, 1);
    next.unshift(moved!);
    onChange(next);
  };

  const canAdd = value.length < maxImages && !disabled;

  const renderTile = (url: string, idx: number, sizeClass: string) => (
    <div key={url} className={cn('relative group rounded-lg overflow-hidden border bg-muted', sizeClass)}>
      <img src={url} alt="" className="w-full h-full object-cover" />
      {idx === 0 && (
        <span className="absolute top-1 start-1 bg-primary text-primary-foreground rounded-full p-0.5">
          <Star size={10} />
        </span>
      )}
      {!disabled && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
          {idx !== 0 && (
            <Button
              type="button"
              size="icon-xs"
              onClick={() => makePrimary(idx)}
              className="size-6 rounded-full bg-white/90 text-primary hover:bg-white"
              title={t('articles.makePrimary')}
              aria-label={t('articles.makePrimary')}
            >
              <Star size={11} />
            </Button>
          )}
          <Button
            type="button"
            size="icon-xs"
            onClick={() => handleRemove(idx)}
            className="size-6 rounded-full bg-white/90 text-destructive hover:bg-white"
            aria-label={t('common.delete')}
          >
            <X size={11} />
          </Button>
        </div>
      )}
    </div>
  );

  const fileInput = canAdd && (
    <Input
      ref={inputRef}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      disabled={!canAdd || uploading}
      onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
    />
  );

  if (variant === 'hero') {
    const [primary, ...rest] = value;
    return (
      <div className={cn('space-y-2', className)}>
        {fileInput}
        {primary ? (
          renderTile(primary, 0, 'aspect-square w-full')
        ) : canAdd ? (
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="aspect-square w-full h-auto flex-col gap-1 border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-primary"
          >
            {uploading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
            <span className="text-xs">{uploading ? t('common.loading') : t('articles.addImage')}</span>
          </Button>
        ) : (
          <div className="aspect-square w-full rounded-lg border bg-muted flex items-center justify-center text-sm text-muted-foreground">
            <ImageIcon size={20} className="me-2" />{t('articles.noImages')}
          </div>
        )}

        {(rest.length > 0 || (primary && canAdd)) && (
          <div className="grid grid-cols-4 gap-2">
            {rest.map((url, i) => renderTile(url, i + 1, 'aspect-square'))}
            {primary && canAdd && (
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="aspect-square h-auto flex-col gap-1 border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-primary"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                <span className="text-[10px]">{uploading ? t('common.loading') : t('articles.addImage')}</span>
              </Button>
            )}
          </div>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((url, idx) => (
          <div key={url} className="relative group rounded-lg overflow-hidden border bg-muted aspect-square">
            <img src={url} alt="" className="w-full h-full object-cover" />
            {idx === 0 && (
              <span className="absolute top-1 start-1 bg-primary text-primary-foreground rounded-full p-0.5">
                <Star size={10} />
              </span>
            )}
            {!disabled && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                {idx !== 0 && (
                  <Button
                    type="button"
                    size="icon-xs"
                    onClick={() => makePrimary(idx)}
                    className="size-6 rounded-full bg-white/90 text-primary hover:bg-white"
                    title={t('articles.makePrimary')}
                    aria-label={t('articles.makePrimary')}
                  >
                    <Star size={11} />
                  </Button>
                )}
                <Button
                  type="button"
                  size="icon-xs"
                  onClick={() => handleRemove(idx)}
                  className="size-6 rounded-full bg-white/90 text-destructive hover:bg-white"
                  aria-label={t('common.delete')}
                >
                  <X size={11} />
                </Button>
              </div>
            )}
          </div>
        ))}

        {canAdd && (
          <>
            <Input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={!canAdd || uploading}
              onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="aspect-square h-auto flex-col gap-1 border-2 border-dashed border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-primary"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              <span className="text-[10px]">{uploading ? t('common.loading') : t('articles.addImage')}</span>
            </Button>
          </>
        )}
      </div>
      {value.length === 0 && !canAdd && (
        <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
          <ImageIcon size={16} className="me-2" />{t('articles.noImages')}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
